import { useId, useRef, useState, type ReactNode } from 'react';
import {
  ADDRESS_PROBLEM_FIELDS,
  ADDRESS_REFUSAL,
  DEFAULT_COUNTRY,
  DOCTOR_FIELDS,
  addressInputOf,
  addressProblems,
  addressShown,
  clinicMissing,
  countryOf,
  doctorPhoneShown,
  doctorProblems,
  doctorRecord,
  sameAddress,
  tidyAddress,
  type AddressProblemField,
  type Doctor,
  type DoctorField,
  type DoctorInput,
} from '@/domain';
import { useAddDoctor, useSaveDoctor } from '@/application/usecases/useClients';
import { useCountries, usePhoneBook } from '@/application/usecases/useGeo';
import { useSession } from '@/application/usecases/useSession';
import { strings } from '@/shared/strings';
import { AddressBlock } from '@/ui/data/AddressBlock';
import { BottomSheet } from '@/ui/primitives/BottomSheet';
import { Button } from '@/ui/primitives/Button';
import { modalStyles } from '@/ui/primitives/Modal';
import { sheetStyles } from '@/ui/primitives/sheetStyles';
import { toast } from '@/ui/primitives/toastStore';
import { useConfirmAction } from '../shell/useConfirmAction';
import { AddressFields, AddressFieldsLoading } from './AddressFields';
import { addressEmpty, type AddressParts } from './addressParts';
import { formRefusal, statusOf } from './clientRefusal';
import { PhoneField } from './PhoneField';
import {
  EMPTY_PHONE,
  oldTextPhone,
  phoneSent,
  recordPhone,
  samePhone,
  type PhoneEntry,
} from './phoneEntry';
import styles from './ClientForm.module.css';

/** The four fields that are a line of text typed as it is kept: everything of the five but the number. */
type TextField = Exclude<DoctorField, 'phone'>;
const TEXT_FIELDS = DOCTOR_FIELDS.filter((field): field is TextField => field !== 'phone');

/** The four fields that may be left empty: everything but the name. */
type Detail = Exclude<DoctorField, 'name'>;
const DETAILS = DOCTOR_FIELDS.filter((field): field is Detail => field !== 'name');

/** Everything the form draws a control for: the five lines of text, then the parts of the address. */
type FormField = DoctorField | AddressProblemField;
const FORM_FIELDS: readonly FormField[] = [...DOCTOR_FIELDS, ...ADDRESS_PROBLEM_FIELDS];
/** What the server may name in a 400: those, and the address as a whole. */
const REFUSED_FIELDS = [...FORM_FIELDS, 'address'] as const;

const FIELD_LABEL: Record<DoctorField, string> = {
  name: strings.clients.fieldName,
  clinic: strings.clients.fieldClinic,
  phone: strings.clients.fieldPhone,
  email: strings.clients.fieldEmail,
  website: strings.clients.fieldWebsite,
};

type Typed = Record<TextField, string>;
type FieldErrors = Partial<Record<FormField | 'address', string>>;

function typedFrom(doctor: Doctor | undefined): Typed {
  return {
    name: doctor?.name ?? '',
    clinic: doctor?.clinic ?? '',
    email: doctor?.email ?? '',
    website: doctor?.website ?? '',
  };
}

/**
 * Asks for the two things a doctor's form loads when it opens, the telephone
 * library and the country list, ahead of the form: drawn by a screen the form
 * is opened from, for whoever may open it, once the screen's own reading is
 * done. The form then opens with both in hand, which is what keeps the number
 * of a record from being spelt one way and then another as the library
 * arrives. Each is loaded once for the life of the page, so this costs
 * nothing the form would not have asked for itself. Draws nothing.
 */
export function WarmDoctorForm() {
  usePhoneBook();
  useCountries();
  return null;
}

export interface DoctorFormSheetProps {
  /** The record being edited. Left off to add a doctor. */
  doctor?: Doctor;
  onClose: () => void;
  /** The record as the server answered it: the new doctor, or the saved one. */
  onDone: (doctor: Doctor) => void;
  /**
   * The copy on screen is out of date: somebody changed the record, or deleted
   * it, while it was being edited. The owner reads it again.
   */
  onStale?: () => void;
}

/**
 * A doctor's record, added or edited: the name, the clinic, the telephone
 * number, the email address, the website and the address, in a sheet. The
 * request of 6 October 2026 (second), items 4 and 5.
 *
 * Only the name is needed. What is typed is checked first, by the server's own
 * rules (`doctorProblems`, `addressProblems`, `clinicMissing`), and every
 * field with something wrong is marked at once, in the server's words. Only a
 * record that would be accepted is asked about, for the roles that are asked:
 * the question lies over the sheet, so Cancel comes back to the form exactly
 * as it was typed.
 *
 * What the server alone can say comes back to where it belongs. A 400 lands on
 * the fields it names, a part of the address among them. A 409 is about the
 * record and not a field, another doctor of the same name and clinic or a copy
 * somebody has changed since, so it is said above the buttons, as a 403 is; a
 * stale copy is also read again, and Save then sends the same edit against the
 * record as it now stands.
 *
 * Renaming is a permission of its own since the request of 6 October 2026
 * (third), item 4, so an edit is two things. The name is a box only for a
 * holder of `renameClients`, and the details only for a holder of
 * `manageClients`; whichever the person may not change is shown as it stands,
 * to read. An edit sends ONLY what changed, with the record's version, so a
 * person with one of the two permissions never sends a field the server asks
 * the other for. Emptying a detail clears it. Nothing changed sends nothing.
 * A NEW doctor is named by whoever adds it, which is no rename: adding shows
 * every box.
 *
 * The number and the address are in an international form since the request
 * of 6 October 2026 (fifth), section 2:
 *
 *   phone    a country code and a number (`PhoneField`), sent as E.164 and
 *            only when the number changed.
 *   address  the parts its country asks for (`AddressFields`), sent whole
 *            whenever any part changed, because the server replaces an address
 *            and never merges one. An address is all or nothing: left empty
 *            it is not sent, emptied it is cleared, and begun it has to be
 *            finished, and then the clinic is needed too.
 *
 * Where the text typed before there was a form is still kept, it is shown
 * above its field as it was ("Entered before as: ..."), to copy from. And
 * where the record has only the old telephone text and it reads as a number,
 * with the lab's own country assumed, the box starts filled from it: not
 * saved, and said to be so, until the person presses Save.
 *
 * The telephone library and the country list are loaded when the form opens.
 * The form does not wait for either: the number is a plain box until its
 * library comes and reads the same afterwards, and the address, which is the
 * last thing in the form, keeps its place with the shape of its fields until
 * the countries are there.
 */
export function DoctorFormSheet({ doctor, onClose, onDone, onStale }: DoctorFormSheetProps) {
  const add = useAddDoctor();
  const save = useSaveDoctor();
  const ask = useConfirmAction();
  const permissions = useSession().data?.permissions ?? [];
  const book = usePhoneBook().data;
  const countryList = useCountries();
  const countries = countryList.data;
  const baseId = useId();
  const inputs = useRef<Partial<Record<FormField, HTMLElement | null>>>({});

  const [typed, setTyped] = useState<Typed>(() => typedFrom(doctor));
  // What the form held when it opened: closing asks first once something differs.
  const [opened] = useState(typed);
  // The number as the person left it. Until they touch the field it is whatever the record gives.
  const [phone, setPhone] = useState<PhoneEntry>(EMPTY_PHONE);
  const [phoneTouched, setPhoneTouched] = useState(false);
  const [address, setAddress] = useState<AddressParts>(() =>
    addressInputOf(doctor?.address ?? null, DEFAULT_COUNTRY),
  );
  const [addressOpened] = useState(address);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [message, setMessage] = useState<string | null>(null);

  const adding = doctor === undefined;
  const mayName = adding || permissions.includes('renameClients');
  const mayDetail = adding || permissions.includes('manageClients');
  const editable = (field: FormField) => (field === 'name' ? mayName : mayDetail);

  /*
   * The number the field starts from: the record's own, or, where the record
   * has only the old text and it reads as a number, that number, unsaved.
   * Worked out again when the telephone library arrives, which changes how it
   * is spelt and nothing else; once the person has typed, the field is theirs.
   */
  const phoneOfRecord = recordPhone(doctor, book);
  const phoneFromOldText = oldTextPhone(doctor, book);
  const phoneStart = phoneFromOldText ?? phoneOfRecord;
  const phoneShown = phoneTouched ? phone : phoneStart;
  const phoneOut = phoneSent(phoneShown, book);
  const phoneChanged = adding
    ? phoneOut !== null
    : mayDetail && (phoneTouched || phoneFromOldText !== null) && phoneOut !== doctor.phone;

  const addressBlank = addressEmpty(address);
  const addressChanged =
    mayDetail &&
    !(addressBlank && addressEmpty(addressOpened)) &&
    !sameAddress(address, addressOpened, countries ?? []);
  // An address that is sent as its parts; emptied, it is sent as null, which clears it.
  const addressSent = (adding || addressChanged) && !addressBlank;

  const pending = add.isPending || save.isPending;
  const dirty =
    TEXT_FIELDS.some((field) => typed[field] !== opened[field]) ||
    (phoneTouched && !samePhone(phone, phoneStart)) ||
    addressChanged;
  const idOf = (field: FormField) => `${baseId}-${field}`;
  const register = (field: FormField) => (element: HTMLElement | null) => {
    inputs.current[field] = element;
  };

  function set(field: TextField, value: string) {
    setTyped((current) => ({ ...current, [field]: value }));
  }

  /** The server's answer to a save it refused, put where the form shows it; the rest travels on to the toast. */
  function refused(error: unknown): void {
    // Changed or deleted by somebody else meanwhile: what is on screen is no longer the record.
    if (!adding && (statusOf(error) === 409 || statusOf(error) === 404)) onStale?.();

    const refusal = formRefusal(error, REFUSED_FIELDS);
    if (refusal === null) throw error;
    setErrors(refusal.fields);
    setMessage(refusal.message);
  }

  function submit() {
    // As the record will keep it: names tidied, details trimmed, a blank one no detail.
    const record = doctorRecord(typed);

    /*
     * Everything wrong, in the order the form shows it. The number is asked
     * about only when it is going to be sent, and the address only when it is
     * sent as its parts: what the record already holds is not held against
     * somebody who came to change something else.
     */
    const problems: { field: FormField; message: string }[] = [
      ...doctorProblems({ ...typed, phone: phoneChanged ? phoneOut : null }, book?.read),
      ...(addressSent ? addressProblems(address, countries ?? []) : []),
    ];
    // An address is delivered to a clinic: asked of a save that sends either, as the server asks it.
    const hasAddress = addressSent || (!adding && !addressChanged && doctor.address !== null);
    const sendsClinic = adding || record.clinic !== doctorRecord(opened).clinic;
    if (
      (addressSent || sendsClinic) &&
      clinicMissing(record.clinic, hasAddress) &&
      !problems.some((problem) => problem.field === 'clinic')
    ) {
      problems.push({ field: 'clinic', message: ADDRESS_REFUSAL.clinicRequired });
    }

    // Only what the person can put right is held against them.
    const theirs = problems.filter((problem) => editable(problem.field));
    setErrors(Object.fromEntries(theirs.map((problem) => [problem.field, problem.message])));
    setMessage(null);
    const first = FORM_FIELDS.find((field) => theirs.some((problem) => problem.field === field));
    if (first !== undefined) {
      inputs.current[first]?.focus();
      return;
    }

    // The nine parts, tidied as the server will tidy them: the whole address, never a part of it.
    const parts = tidyAddress(address, countryOf(countries ?? [], address.country));

    if (adding) {
      const input: DoctorInput = { name: record.name };
      for (const detail of DETAILS) {
        if (detail === 'phone') {
          if (phoneOut !== null) input.phone = phoneOut;
        } else if (record[detail] !== null) input[detail] = record[detail];
      }
      if (addressSent) input.address = parts;
      ask(
        strings.clients.confirmAddDoctor(record.name),
        () =>
          add.mutateAsync(input).then((made) => {
            toast(strings.clients.doctorAdded(made.name), { tone: 'success' });
            onDone(made);
          }, refused),
        { title: strings.clients.addDoctorTitle, confirmLabel: strings.clients.addDoctor },
      );
      return;
    }

    /*
     * What changed, and nothing else. The log says what was done field by
     * field, and the server asks for renameClients on a name that differs and
     * for manageClients on any detail that is sent at all, changed or not: so a
     * field the person could not edit is never in the body.
     */
    const before = doctorRecord(opened);
    const changes: Partial<DoctorInput> = {};
    if (mayName && record.name !== doctor.name) changes.name = record.name;
    if (mayDetail) {
      for (const detail of DETAILS) {
        if (detail === 'phone') {
          if (phoneChanged) changes.phone = phoneOut;
        } else if (record[detail] !== before[detail]) changes[detail] = record[detail];
      }
      if (addressChanged) changes.address = addressBlank ? null : parts;
    }
    const changed = [
      ...DETAILS.filter((detail) => detail in changes).map((detail) => FIELD_LABEL[detail]),
      ...(changes.address === undefined ? [] : [strings.address.title]),
    ];
    if (changes.name === undefined && changed.length === 0) {
      onClose();
      return;
    }

    ask(
      changes.name === undefined
        ? strings.clients.confirmSaveDoctor(doctor.name, changed)
        : strings.clients.confirmRenameDoctor(doctor.name, changes.name, changed),
      () =>
        save
          .mutateAsync({ id: doctor.id, rowVersion: doctor.rowVersion, changes })
          .then((saved) => {
            toast(strings.clients.doctorSaved(saved.name), { tone: 'success' });
            onDone(saved);
          }, refused),
      { title: strings.clients.editDoctorTitle(doctor.name), confirmLabel: strings.clients.save },
    );
  }

  /** What each of the four plain boxes carries: its id, its value, its error, and where focus finds it. */
  function box(field: TextField) {
    return {
      id: idOf(field),
      value: typed[field],
      'aria-invalid': errors[field] !== undefined || undefined,
      'aria-describedby': errors[field] === undefined ? undefined : `${idOf(field)}-error`,
      ref: register(field),
      onChange: (event: { target: { value: string } }) => set(field, event.target.value),
    };
  }

  /** The text typed before a field had a form, above the field, as it was: to read and to copy from. */
  function oldText(text: string | null, review: boolean, reviewHint: string): ReactNode {
    if (text === null && !review) return null;
    return (
      <div className={styles.before}>
        {text !== null && <p className={styles.beforeText}>{strings.clients.enteredBefore(text)}</p>}
        {review && (
          <p className={styles.review}>
            <span className={styles.mark}>{strings.address.needsReview}</span> {reviewHint}
          </p>
        )}
      </div>
    );
  }

  /** A detail the person may not change, as it stands, to read. */
  function fixed(name: string, label: string, shown: ReactNode, locked?: string) {
    return (
      <div className={modalStyles.field}>
        <span className={modalStyles.label}>{label}</span>
        <div className={styles.fixed} data-testid={`fixed-${name}`}>
          {shown ?? <span className={styles.unset}>{strings.clients.notSet}</span>}
        </div>
        {locked !== undefined && <span className={modalStyles.hint}>{locked}</span>}
      </div>
    );
  }

  /**
   * A field as a box to type in, or, for somebody who may not change it, as it
   * stands, to read. `locked` says why there is no box, under the field it is
   * given to. `above` goes between the label and the box.
   */
  function field(
    name: DoctorField,
    control: ReactNode,
    options: { locked?: string; above?: ReactNode; required?: boolean; shown?: string | null } = {},
  ) {
    if (!editable(name)) {
      const shown = options.shown === undefined ? typed[name as TextField] : (options.shown ?? '');
      return fixed(name, FIELD_LABEL[name], shown === '' ? null : shown, options.locked);
    }
    return (
      <div className={modalStyles.field}>
        <label
          className={`${modalStyles.label} ${options.required === true ? styles.required : ''}`}
          htmlFor={idOf(name)}
        >
          {FIELD_LABEL[name]}
        </label>
        {options.above}
        {control}
        {errors[name] !== undefined && (
          <p id={`${idOf(name)}-error`} className={modalStyles.error}>
            {errors[name]}
          </p>
        )}
      </div>
    );
  }

  const shownAddress = doctor === undefined ? null : addressShown(doctor);
  const addressHeadingId = `${baseId}-address`;

  return (
    <BottomSheet
      title={adding ? strings.clients.addDoctorTitle : strings.clients.editDoctorTitle(doctor.name)}
      lead={
        adding
          ? strings.clients.addDoctorLead
          : mayName
            ? strings.clients.editDoctorLead
            : strings.clients.editDetailsLead
      }
      closeLabel={strings.sheet.close}
      onClose={onClose}
      // As tall as the screen allows from the start, so the sheet does not grow and shift on a
      // desk as the address is filled in and its label is drawn under it.
      tall={mayDetail}
      unsaved={
        dirty
          ? {
              question: strings.clients.discardQuestion,
              keep: strings.sheet.keep,
              discard: strings.sheet.discard,
            }
          : null
      }
      footer={
        <>
          {message !== null && (
            <p className={styles.message} role="alert">
              {message}
            </p>
          )}
          <div className={sheetStyles.actions}>
            <Button onClick={onClose} disabled={pending}>
              {strings.clients.cancel}
            </Button>
            <Button
              variant="primary"
              className={sheetStyles.primary}
              loading={pending}
              onClick={submit}
            >
              {strings.clients.save}
            </Button>
          </div>
        </>
      }
    >
      <div className={styles.form}>
        {field(
          'name',
          <input
            {...box('name')}
            className={modalStyles.input}
            autoComplete="off"
            autoCapitalize="words"
            placeholder={strings.clients.doctorNamePlaceholder}
            aria-required="true"
            data-autofocus
          />,
          { locked: strings.clients.nameLocked },
        )}
        {field(
          'clinic',
          <input
            {...box('clinic')}
            className={modalStyles.input}
            autoComplete="off"
            autoCapitalize="words"
            placeholder={strings.clients.clinicPlaceholder}
            // Needed once there is an address: an address is delivered to a clinic or a company.
            aria-required={!addressBlank || undefined}
            // The first box of the form, for somebody who edits the details and not the name.
            data-autofocus={mayName ? undefined : true}
          />,
          { required: !addressBlank },
        )}
        {field(
          'phone',
          <PhoneField
            id={idOf('phone')}
            value={phoneShown}
            onChange={(next) => {
              setPhone(next);
              setPhoneTouched(true);
            }}
            book={book}
            countries={countries}
            invalid={errors.phone !== undefined}
            describedBy={errors.phone === undefined ? undefined : `${idOf('phone')}-error`}
            inputRef={register('phone')}
          />,
          {
            shown: doctor === undefined ? null : doctorPhoneShown(doctor),
            above: (
              <>
                {oldText(
                  doctor?.mobile ?? null,
                  doctor?.phoneNeedsReview === true,
                  strings.phone.reviewHint,
                )}
                {/* Filled from the old text, and nobody has typed over it: said, so it is not taken for saved. */}
                {phoneFromOldText !== null && !phoneTouched && (
                  <p className={styles.unsaved}>{strings.phone.prefilled}</p>
                )}
              </>
            ),
          },
        )}
        {field(
          'email',
          <input
            {...box('email')}
            className={modalStyles.input}
            type="email"
            inputMode="email"
            autoComplete="off"
            autoCapitalize="none"
            spellCheck={false}
          />,
        )}
        {field(
          'website',
          <input
            {...box('website')}
            className={modalStyles.input}
            inputMode="url"
            autoComplete="off"
            autoCapitalize="none"
            spellCheck={false}
            placeholder={strings.clients.websitePlaceholder}
          />,
        )}

        {mayDetail ? (
          <div className={styles.group} role="group" aria-labelledby={addressHeadingId}>
            <h4 id={addressHeadingId} className={styles.groupTitle}>
              {strings.address.title}
            </h4>
            <p className={styles.groupLead}>{strings.address.lead}</p>
            {oldText(
              doctor?.deliveryAddress ?? null,
              doctor?.addressNeedsReview === true,
              strings.address.reviewHint,
            )}
            {errors.address !== undefined && (
              <p className={modalStyles.error} role="alert">
                {errors.address}
              </p>
            )}
            {countries !== undefined ? (
              <AddressFields
                value={address}
                onChange={setAddress}
                countries={countries}
                errors={errors}
                name={typed.name}
                clinic={typed.clinic}
                register={register}
              />
            ) : countryList.isError ? (
              <p className={styles.unavailable} role="alert">
                {strings.address.loadFailed}
                <Button onClick={() => void countryList.refetch()}>{strings.errors.retry}</Button>
              </p>
            ) : (
              <AddressFieldsLoading />
            )}
          </div>
        ) : (
          fixed(
            'address',
            strings.address.title,
            shownAddress === null ? null : shownAddress.kind === 'lines' ? (
              <AddressBlock lines={shownAddress.lines} />
            ) : (
              shownAddress.text
            ),
            // Said once, under the last of the details it is about.
            strings.clients.detailsLocked,
          )
        )}
      </div>
    </BottomSheet>
  );
}
